import { ChangeEvent, useState, useTransition } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Information, Settings } from '@mint-lib/icons';
import { typographyVariants } from '@mint-lib/tw-components';
import { SearchBar } from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';

import type { GlossaryItem, PlatformCatalogItem } from '../../api/types.js';
import { usePlatformsCatalog } from '../../queries/index.js';
import { EmptyState } from '../atoms/EmptyState.jsx';
import { Tooltip } from '../atoms/Tooltip.jsx';
import type {
  PlatformGlossaryOption,
  PlatformGlossaryOptionsGroup,
} from '../types.js';
import { GlossarySelectionListItem } from './GlossarySelectionListItem.jsx';

type PlatformsGlossaryListV1Props = {
  glossaries: GlossaryItem[];
  selectedItems: string[];
  onItemSelect: (name: string) => () => void;
};

export const PlatformsGlossaryListV1 = ({
  glossaries,
  selectedItems,
  onItemSelect,
}: PlatformsGlossaryListV1Props) => {
  const { t } = useTranslation('@myn/import-hub');

  const {
    data: { platformsList },
  } = usePlatformsCatalog();

  const [, startTransition] = useTransition();

  const [searchValue, setSearchValue] = useState('');

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    startTransition(() => {
      setSearchValue(e.target.value);
    });
  };
  const handleSearchClear = () => {
    setSearchValue('');
  };

  const filteredPlatformsList = platformsList.reduce((acc, platform) => {
    const isMatchingSearchValue = platform.label
      .trim()
      .toLowerCase()
      .includes(searchValue.trim().toLowerCase());

    if (isMatchingSearchValue) {
      acc.push({
        name: platform.name,
        label: platform.label,
        logo: platform.logo ? (
          <img src={platform.logo} width={16} alt="platform logo" />
        ) : (
          <Settings className="fill-icon-primary" />
        ),
        type: platform.type,
        readOnly: glossaries.some(
          function isPlatformSelected(platformGlossary) {
            return platformGlossary.name === platform.name;
          },
        ),
        isIntegrated: platform.isIntegrated ?? false,
      });
    }
    return acc;
  }, [] as PlatformGlossaryOption[]);

  const initialPlatformsByType: Record<
    NonNullable<PlatformCatalogItem['type']>,
    PlatformGlossaryOptionsGroup
  > = {
    two_way: {
      label: t('TWO-WAY'),
      items: [] as PlatformGlossaryOption[],
    },
    one_way: {
      label: t('ONE-WAY'),
      items: [] as PlatformGlossaryOption[],
    },
    virtual: {
      label: t('VIRTUAL'),
      items: [] as PlatformGlossaryOption[],
    },
  };
  const notIntegratedPlatforms: PlatformGlossaryOption[] = [];

  const integratedPlatformsByType = filteredPlatformsList.reduce(
    (acc, platform) => {
      if (platform.isIntegrated) {
        acc[
          platform.type as NonNullable<PlatformCatalogItem['type']>
        ].items.push(platform);
      } else {
        notIntegratedPlatforms.push(platform);
      }
      return acc;
    },
    initialPlatformsByType,
  );

  const groups = Object.values(integratedPlatformsByType).filter((group) =>
    Boolean(group.items.length),
  );

  const isNotIntegratedPlatformsShown =
    searchValue.length > 0 && notIntegratedPlatforms.length;

  return (
    <>
      <SearchBar
        value={searchValue}
        onChange={handleSearchChange}
        onClear={handleSearchClear}
        placeholder={t('Search by name')}
        size="small"
        className="mb-2 min-h-8 w-full"
      />
      <div className="flex h-full flex-col gap-6">
        {groups.map((group) => (
          <div key={group.label}>
            <h5
              className={cn(
                typographyVariants({ variant: 'h5', color: 'secondary' }),
                'mb-3',
              )}
            >
              {group.label}
            </h5>
            <div className="flex flex-col gap-2">
              {group.items.map(({ name, label, logo, readOnly }) => (
                <GlossarySelectionListItem
                  key={name}
                  glossaryType="platform"
                  label={label}
                  icon={logo}
                  readOnly={readOnly}
                  selected={selectedItems.includes(name)}
                  onSelect={onItemSelect(name)}
                />
              ))}
            </div>
          </div>
        ))}
        {isNotIntegratedPlatformsShown ? (
          <div>
            <div className="mb-3 flex items-center gap-2">
              <h5
                className={typographyVariants({
                  variant: 'h5',
                  color: 'secondary',
                })}
              >
                {t('NOT INTEGRATED')}
              </h5>
              <Tooltip
                asChild
                description={t(
                  'This platform is not integrated. To do so go to the integration page',
                )}
              >
                <Information className="fill-icon-secondary" />
              </Tooltip>
            </div>
            <div className="flex flex-col gap-2">
              {notIntegratedPlatforms.map(({ name, label, logo }) => (
                <GlossarySelectionListItem
                  key={name}
                  glossaryType="platform"
                  label={label}
                  icon={logo || <Settings className="fill-icon-primary" />}
                  disabled
                />
              ))}
            </div>
          </div>
        ) : null}
        {searchValue && !filteredPlatformsList.length ? (
          <div className="flex h-full flex-col justify-center">
            <EmptyState
              icon="IllustrationNoResults"
              text={t('No results found for "{{searchValue}}"', {
                searchValue,
              })}
              description={t('Please, try searching for something different.')}
              className="border-none shadow-none"
            />
          </div>
        ) : null}
      </div>
    </>
  );
};
